import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { AvailabilityDay, AvailabilityRule } from '~/features/provider-hlx/calendar/types';
import { formatDisplayDate } from '~/features/shared/calendar/dateUtils';
import { buildCalendarUrl } from '~/routing';
import type { AvailabilityMode, ServiceListing } from './types';

interface AvailabilitySummarySectionProps {
  calendarDays: AvailabilityDay[];
  calendarEnabled: boolean;
  calendarError: string | null;
  isCalendarLoading: boolean;
  mode: AvailabilityMode;
  onModeChange: (mode: AvailabilityMode) => void;
  recurringRules: AvailabilityRule[];
  selectedListing?: ServiceListing;
}

const SectionHeader = ({ children }: { children: ReactNode }) => (
  <div className="flex items-start gap-3 text-left">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50">
      <CalendarDaysIcon className="h-5 w-5 text-white" />
    </div>
    <div>
      <h2 className="text-[1rem] font-semibold text-content-primary">Availability Calendar</h2>
      <p className="mt-1 text-sm text-content-secondary">{children}</p>
    </div>
  </div>
);

export const AvailabilitySummarySection = ({
  calendarDays,
  calendarEnabled,
  calendarError,
  isCalendarLoading,
  mode,
  onModeChange,
  recurringRules,
  selectedListing,
}: AvailabilitySummarySectionProps) => {
  const selectedListingId = selectedListing?.id;
  const isGeneralSelected = mode === 'GENERAL';
  const isListingSelected = mode === 'LISTING';
  const matchesMode = (serviceListingId: number | null) =>
    mode === 'GENERAL'
      ? serviceListingId === null
      : selectedListingId != null && serviceListingId === selectedListingId;
  const availableDays = calendarDays.filter((day) =>
    day.entries.some((entry) => entry.status === 'AVAILABLE' && matchesMode(entry.serviceListingId))
  );
  const ruleCount = recurringRules.filter((rule) => matchesMode(rule.serviceListingId)).length;
  const nextAvailableDate = availableDays[0]?.date;
  const calendarUrl = buildCalendarUrl({
    serviceListingId: mode === 'GENERAL' ? 'general' : selectedListingId,
  });
  const canOpenCalendar = calendarEnabled && (mode === 'GENERAL' || selectedListingId != null);

  return (
    <section
      className="rounded-lg border border-grey-900 bg-white p-5 text-left shadow-sm"
      data-cy="provider-service-availability-summary"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeader>
          Availability has one source of truth. Choose the general or listing-specific Calendar view
          you want to manage.
        </SectionHeader>
        {canOpenCalendar ? (
          <Link
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-primary-700"
            data-cy="provider-service-open-calendar"
            to={calendarUrl}
          >
            Open Calendar
          </Link>
        ) : null}
      </div>

      {!calendarEnabled ? (
        <div className="mt-5 rounded-md bg-grey-925 px-4 py-3 text-sm text-content-secondary">
          Calendar management is not enabled for this account yet. Your listing preferences remain
          available here.
        </div>
      ) : (
        <>
          <fieldset className="mt-5 grid gap-3 md:grid-cols-2">
            <legend className="sr-only">Availability scope</legend>
            <label
              className={`cursor-pointer rounded-lg border p-4 ${
                isGeneralSelected ? 'border-primary-50 bg-primary-50 text-white' : 'border-grey-800'
              }`}
            >
              <input
                checked={isGeneralSelected}
                className="mr-2"
                data-cy="provider-service-availability-general"
                name="availability-mode"
                onChange={() => onModeChange('GENERAL')}
                type="radio"
              />
              <span
                className={`font-semibold ${isGeneralSelected ? 'text-white' : 'text-content-primary'}`}
              >
                General availability
              </span>
              <p
                className={`mt-1 pl-6 text-sm ${isGeneralSelected ? 'text-white' : 'text-content-secondary'}`}
              >
                Applies across all of your public service listings.
              </p>
            </label>
            <label
              className={`rounded-lg border p-4 ${
                selectedListing ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              } ${
                isListingSelected ? 'border-primary-50 bg-primary-50 text-white' : 'border-grey-800'
              }`}
            >
              <input
                checked={isListingSelected}
                className="mr-2"
                data-cy="provider-service-availability-listing"
                disabled={!selectedListing}
                name="availability-mode"
                onChange={() => onModeChange('LISTING')}
                type="radio"
              />
              <span
                className={`font-semibold ${isListingSelected ? 'text-white' : 'text-content-primary'}`}
              >
                Listing-specific
              </span>
              <p
                className={`mt-1 pl-6 text-sm ${isListingSelected ? 'text-white' : 'text-content-secondary'}`}
              >
                {selectedListing
                  ? `Adds availability only for ${selectedListing.title}.`
                  : 'Save or select a listing before adding listing-specific availability.'}
              </p>
            </label>
          </fieldset>

          <p className="mt-3 text-sm text-content-secondary">
            This choice controls which Calendar scope opens. Employer matching combines general
            availability with this listing's additions and counts identical entries once.
          </p>

          <div className="mt-4 rounded-lg bg-grey-925 p-4" aria-live="polite">
            {isCalendarLoading ? (
              <p className="text-sm text-content-secondary">Loading availability summary...</p>
            ) : calendarError ? (
              <p className="text-sm text-warning-950">{calendarError}</p>
            ) : (
              <dl className="grid gap-4 sm:grid-cols-3">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                    Available Days
                  </dt>
                  <dd className="mt-1 text-lg font-semibold text-content-primary">
                    {availableDays.length}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                    Recurring Rules
                  </dt>
                  <dd className="mt-1 text-lg font-semibold text-content-primary">{ruleCount}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                    Next Available
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-content-primary">
                    {nextAvailableDate ? formatDisplayDate(nextAvailableDate) : 'Not set'}
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </>
      )}
    </section>
  );
};
